import {
  computed, onMounted, ref,
} from 'vue';
import { fetchSmsPricing } from '@/composables/billing/billingApi';
import { SmsPricingResponse, SmsPricingCountry } from '@/types/billing-types';
import { countriesList } from '@/components/template-builder/utils/countries-list';

// Build a lookup map { code → flag } from the shared countriesList. Aliases
// the legacy `UK` → `GB` (some SMS pricing grids use either). Falls back to
// a globe when the code is unknown.
const FLAG_BY_CODE: Record<string, string> = countriesList.reduce(
  (acc, c) => {
    acc[c.code] = c.flag;
    return acc;
  },
  {} as Record<string, string>,
);
FLAG_BY_CODE.UK = FLAG_BY_CODE.UK ?? FLAG_BY_CODE.GB;

const flagEmoji = (code: string) => FLAG_BY_CODE[code] ?? '🌐';

export type Rate = 'inPlan' | 'oneShoot';

/**
 * Shared SMS pricing + country helpers used by:
 *   - AddCreditsStep (subscription credits, in-plan rate)
 *   - WalletTopUpSelection (one-shot credits, one-shoot rate)
 *
 * Encapsulates: fetch on mount, country dropdown options, flag lookup,
 * selected country data, and estimated SMS count from a given amount.
 *
 * @param rate Which Adyen rate to use for the SMS estimation.
 *             - 'inPlan' for subscription-included credits
 *             - 'oneShoot' for standalone purchases
 */
export function useSmsCountries(rate: Rate = 'oneShoot') {
  const pricing = ref<SmsPricingResponse | null>(null);
  const selectedCountry = ref<string>('FR');

  onMounted(async () => {
    pricing.value = await fetchSmsPricing();
  });

  const countryOptions = computed(() => (
    pricing.value?.countries.map((c) => ({
      value: c.country,
      label: c.countryLabel,
    })) ?? []
  ));

  const selectedCountryData = computed<SmsPricingCountry | null>(() => (
    pricing.value?.countries.find((c) => c.country === selectedCountry.value) ?? null
  ));

  const unitPrice = computed(() => {
    const c = selectedCountryData.value;
    if (!c) return 0;
    return rate === 'inPlan' ? c.unitPriceInPlan : c.unitPriceOneShoot;
  });

  const countryLabelOf = (code: string) => (
    pricing.value?.countries.find((c) => c.country === code)?.countryLabel ?? code
  );

  /**
   * Number of SMS that the given amount (€) can buy, at the selected
   * country's unit price. Returns 0 when prices are unknown or amount is 0.
   */
  const estimatedSmsCount = (amount: number) => {
    const price = unitPrice.value;
    if (!amount || price <= 0) return 0;
    return Math.floor(amount / price);
  };

  return {
    pricing,
    selectedCountry,
    selectedCountryData,
    countryOptions,
    unitPrice,
    countryLabelOf,
    flagEmoji,
    estimatedSmsCount,
  };
}
